import ReactTooltip from "react-tooltip";
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actionError, actionSuccess, hideLoader, showLoader } from "../../../../../../../../middleware/actions/utilityAction";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as Yup from "yup";
import { TextField } from "../../../../../../../components/CustomFormElements/syncFusionFormFields";
import { addDocumentValidationSchema, editDocumentValidationSchema } from "../../../../../../../helper/validationSchema/productValidationSchema";
import {
  addProductDocument,
  editProductDocument,
  fetchDocumentByDocumentId,
  fetchProductDocumentUrl,
} from "../../../../../../../../middleware/services/productApi";
import { ckEditorMinimalConfig } from "../../../../../../../../config/ckEditorConfig";
import { useHistory } from "react-router-dom";
import { formattedDate } from "utils";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";
import { fetchSingleCompany } from "middleware/services/companyApi";
import PropTypes from "prop-types";
import { accessRightActionCheck } from "utilities";
import { COMMON_ACCESS_ACTION } from "../../../../../../../../constants";
import { CkEditorVideo } from "app/components/CustomFormElements/ProductInformation";

const UploadComponent = memo((props) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { acceptedFilesFormat: [".pdf,.docs,.doc,.docx.docx,.xlsx,.xls,.pptx,.txt"] },
    multiple: false,
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const reason = fileRejections[0]?.errors[0]?.code;
        let errorMsg = "Invalid file";

        if (reason === "file-invalid-type") {
          errorMsg = "Unsupported file type";
        } else if (reason === "file-too-large") {
          errorMsg = "File exceeds maximum allowed size of 10MB";
        } else if (reason === "too-many-files") {
          errorMsg = "Only 1 file can be uploaded at a time";
        }

        props.formik.setTouched({ ...props.formik.touched, documentFile: true });
        props.formik.setFieldError("documentFile", errorMsg);
        props.setDocumentError(errorMsg);
      } else {
        props.setDocumentError("");
        const currentFiles = props.formik.values.documentFile || [];
        const newFileName = acceptedFiles[0]?.name;

        if (!currentFiles.length || currentFiles[0]?.name !== newFileName) {
          props.formik.setValues({
            ...props.formik.values,
            documentFile: acceptedFiles,
          });
        }
      }
    },
  });

  const fileIcon = {
    pdf: "pdf.svg",
    doc: "doc-icon.svg",
    docx: "doc-icon.svg",
    xlsx: "/pngs/empty-excel.png",
    xls: "/pngs/empty-excel.png",
    csv: "/pngs/CSV-file-icon.png",
    pptx: "ppt.svg",
    txt: "txtfile-icon.svg",
  };

  return (
    <>
      <p className="mb-1" style={{ fontSize: "1rem", fontWeight: "600", color: "#000" }}>
        Upload Document *
      </p>
      <div className="d-flex justify-content-between">
        <div className="form-group border rounded w-25" {...getRootProps()}>
          <input {...getInputProps()} />
          <button type="button" className="btn btn-grey w-100 py-2" style={{ fontSize: ".8rem", color: "#4B4B55" }}>
            <img src="/assets/upload-icon.svg" alt="upload-file" style={{ marginRight: "1rem" }} />
            <span>
              Drag and Drop or <b>Browse</b>
            </span>
          </button>
        </div>
        <div className="w-50">
          {props.formik.values?.documentFile &&
            props.formik.values?.documentFile.map((file, i) => (
              <div
                key={i}
                className="d-flex justify-content-between align-items-top border rounded p-2"
                style={{ maxWidth: "100%", overflow: "hidden" }}
              >
                <div className="d-flex justify-content-start" style={{ gap: ".5rem", overflow: "hidden" }}>
                  <div className="me-1">
                    <img src={`/assets/${fileIcon[file.name.split(".").pop().toLowerCase()]}`} alt="file" style={{ height: "2rem" }} />
                  </div>
                  <div style={{ overflow: "hidden", lineHeight: 1 }}>
                    <p
                      className="m-0 p-0 text-truncate"
                      style={{
                        fontSize: ".8rem",
                        color: "#000",
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      data-tip={file.name}
                      data-for="file-name-tooltip"
                    >
                      {file.name.includes(".") ? file.name.substring(0, file.name.lastIndexOf(".")) : file.name}
                    </p>
                    <span style={{ fontSize: ".75rem", color: "#939399", lineHeight: 1, marginTop: ".3rem" }}>
                      {formattedDate(file.lastModifiedDate)}
                    </span>
                  </div>
                </div>
                <div style={{ marginLeft: ".5rem" }}>
                  <img
                    className="cursor-pointer"
                    src="/assets/trash-box.svg"
                    alt="trash-box"
                    onClick={() => props.formik.setFieldValue("documentFile", [])}
                  />
                </div>
                <ReactTooltip id="file-name-tooltip" effect="solid" place="top" delayShow={300} style={{ maxWidth: "200px" }} />
              </div>
            ))}
          {props?.editData && props.formik.values.linkFile && !props.formik.values?.documentFile && (
            <>
              <div className="d-flex align-items-center border rounded p-2" style={{ overflow: "hidden" }}>
                <a
                  href={fetchProductDocumentUrl(props.formik.values.linkFile, props.formik.values.documentFileName)}
                  className="text-truncate flex-grow-1 me-2"
                  style={{ maxWidth: "calc(100% - 30px)" }}
                  data-tip={props.formik.values.documentFileName}
                  data-for="file-name-tooltip"
                >
                  <img src="/assets/download-icon.svg" alt="image-download" style={{ marginRight: "4px" }} />
                  {props.formik.values.documentFileName}
                </a>
                <div className="ms-2">
                  <img
                    className="cursor-pointer"
                    src="/assets/trash-box.svg"
                    alt="trash-box"
                    onClick={() => props.formik.setFieldValue("linkFile", "")}
                    style={{ minWidth: "20px" }}
                  />
                </div>
                <ReactTooltip id="file-name-tooltip" effect="solid" place="top" delayShow={300} />
              </div>
            </>
          )}
        </div>
      </div>
      <small style={{}}>
        <strong>Note: </strong>
        <ul className="" style={{ marginLeft: "1.5rem" }}>
          <li>Accepts only pdf, doc, docs, xlsx, xls, csv, pptx, txt files.</li>
          <li>Maximum file size allowed for upload: 10 MB</li>
        </ul>
      </small>
      <div className="text-danger" style={{ display: props.formik.touched.documentFile === true ? "block" : "none" }} role="alert">
        <small>{props.formik.touched.documentFile === true ? props.documentError || "" : ""}</small>
      </div>
    </>
  );
});

const DocumentModal = (props) => {
  const { companyId, productId, documentId } = props.computedMatch?.params || {};
  const { accessRights: aR } = props;
  const [oldData, setOldData] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [productName, setProductName] = useState("");
  const [interfaceActionAccess, setInterfaceActionAccess] = useState([]);
  const lastSetValues = useRef(null);
  const { productDetails = {} } = useSelector((state) => state.productState);
  const [documentError, setDocumentError] = useState("");

  const split_url = props.location?.pathname.split("/");
  const backPath = props?.editData ? `${split_url.slice(0, -3).join("/")}/edit#overview` : `${split_url.slice(0, -2).join("/")}/edit#overview`;
  const history = useHistory();

  const initialFormValues = {
    documentTitle: "",
    documentDescription: "",
    linkFile: "",
    documentFile: "",
    documentFileName: "",
  };

  const breadCrumbLinks = useMemo(() => {
    const links = [{ linkUrl: "/company", linkName: `Company`, linkActive: false }];
    if (companyId && companyName) {
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: companyName, linkActive: false });
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: aR.moduleName, linkActive: false });
    }

    if (productId && productName) {
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: productName,
        linkActive: false,
      });
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: "Document",
        linkActive: false,
      });
      links.push({ linkUrl: ``, linkName: props.editData ? "Edit" : "Add New", linkActive: true });
    }

    return links;
  }, [companyId, companyName, productId, productName]);

  /* Interface action access from common access */
  useEffect(() => {
    let interfaceName = "Overview";
    let actionAccess = accessRightActionCheck(aR.moduleName, interfaceName);
    setInterfaceActionAccess(actionAccess);
  }, []);

  /* Common access */
  const accessPermissionAdd = interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.ADD_DOCUMENTS);
  const accessPermissionEdit = interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.EDIT_DOCUMENTS);

  const formik = useFormik({
    initialValues: {
      ...initialFormValues,
    },
    validationSchema: Yup.object().shape(props.editData ? editDocumentValidationSchema : addDocumentValidationSchema),
  });
  const setFormikValues = useCallback(
    (data) => {
      const newData = {
        documentTitle: data.docTitle ? data.docTitle : "",
        documentDescription: data.docDescription || "",
        linkFile: data.docFileName || "",
        documentFile: "",
        documentFileName: data.docName || "",
      };
      setOldData((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(newData)) {
          return newData;
        }
        return prev;
      });
      if (JSON.stringify(lastSetValues.current) !== JSON.stringify(newData)) {
        formik.setValues(newData);
        lastSetValues.current = newData;
      }
    },
    [formik]
  );

  const dispatch = useDispatch();
  const fetchDoc = useCallback(async () => {
    try {
      const response = await fetchDocumentByDocumentId(productId, documentId);
      setFormikValues(response.data.data[0]);
    } catch (error) {
      dispatch(actionError("Something Went Wrong while processing."));
    }
  }, [productId, documentId, dispatch]);

  useEffect(() => {
    if (props.editData) {
      fetchDoc();
    } else {
      formik.resetForm();
      setOldData(initialFormValues);
      lastSetValues.current = initialFormValues;
    }
  }, [props.editData, fetchDoc, dispatch]);

  const handleReset = () => {
    formik.resetForm();
    if (props.editData && oldData) {
      formik.setValues(oldData);
      lastSetValues.current = oldData;
    }
  };

  const onUpdateData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }
    try {
      dispatch(showLoader());
      await editProductDocument(productId, documentId, formData);
      dispatch(hideLoader());
      dispatch(actionSuccess("Product document updated successfully."));
      history.push(backPath, { shouldRefresh: true });
    } catch (err) {
      dispatch(actionError(err ? err.data.message : "Something went wrong."));
    } finally {
      handleReset();
      dispatch(hideLoader());
    }
  };

  const onSaveData = async (data) => {
    if (productDetails?.productDocuments?.length >= 10) return dispatch(actionError("You can not add more than 10 documents."));
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }

    try {
      dispatch(showLoader());
      await addProductDocument(productId, formData);
      dispatch(hideLoader());
      dispatch(actionSuccess("Product document saved successfully."));
      history.push(backPath, { shouldRefresh: true });
    } catch (err) {
      dispatch(actionError(err ? err.data.message : "Something went wrong."));
    } finally {
      handleReset();
      dispatch(hideLoader());
    }
  };

  const saveDocumentData = async () => {
    const { values } = formik;
    const productDocumentData = {
      docTitle: values.documentTitle,
      docDescription: values.documentDescription,
      file: values.documentFile[0],
    };
    if (props.editData) {
      if (!values.documentFile[0]) delete productDocumentData.file;
    }
    props?.editData ? onUpdateData(productDocumentData) : onSaveData(productDocumentData);
  };
  const handleSubmitDocument = () => {
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).forEach((field) => {
          touched[field] = true;
        });
        formik.setFormikState({ ...formik, touched: touched, errors: res });
        setDocumentError(res?.documentFile);
      } else {
        saveDocumentData();
      }
    });
  };

  const getCompanyName = async () => {
    if (companyId) {
      if (Object.keys(productDetails).length > 0) {
        setCompanyName(productDetails.productCompany.companyName);
      } else {
        const fields = { fields: ["id", "companyName"] };
        try {
          const response = await fetchSingleCompany(companyId, { ...fields });
          setCompanyName(response.data.data.companyName);
        } catch (error) {
          dispatch(actionError(error.data.message || "something went wrong."));
        }
      }
    }
  };

  useEffect(() => {
    setProductName(productDetails.productName);
    getCompanyName();
  }, [productDetails, dispatch]);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));

    return () => {
      dispatch(setBreadCrumb([]));
    };
  }, [dispatch, breadCrumbLinks]);

  return (
    <div className="p-0 mx-3">
      <div className="p-2 border custom-bg-grey text-white rounded-top">Products &amp; Technology - Documents/ Brochure</div>
      <div className="d-flex flex-column justify-content-between border border-top-0" style={{ height: "82vh" }}>
        <form className="p-4">
          <div className="mb-4">
            <div style={{ width: "40%" }}>
              <TextField formik={formik} id="documentTitle" name="documentTitle" placeholder="Name *" />
            </div>
            <div className="d-flex flex-row col-12 m-0 p-0">
              <div className="col-6 px-0" style={{ marginRight: "1rem", maxHeight: "300px !important" }}>
                <label>Description *</label>
                <CkEditorVideo
                  formik={formik}
                  properties={{
                    id: "documentDescription",
                    name: "documentDescription",
                    activeClass: "documentDescription",
                    label: "Description *",
                    config: ckEditorMinimalConfig,
                    charCountLimit: 1000,
                    height: "200px !important",
                  }}
                />
              </div>

              <div className="col-6">
                <UploadComponent editData={props.editData} formik={formik} documentError={documentError} setDocumentError={setDocumentError} />
              </div>
            </div>
          </div>
        </form>
        <div className="text-right border-top p-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              history.push(backPath);
            }}
            className="btn btn-outline-dark mr-3 company-button"
          >
            Cancel
          </button>
          {(accessPermissionAdd || (accessPermissionEdit && props?.editData)) && (
            <button type="button" className="btn btn-primary company-button" onClick={handleSubmitDocument} disabled={false}>
              {props?.editData ? "Update" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentModal;

DocumentModal.propTypes = {
  accessRights: PropTypes.object,
  location: PropTypes.object,
  computedMatch: PropTypes.object,
  editData: PropTypes.bool,
};
